import { useEffect, useRef, useState } from 'react';
import {
  SignInCancelled, appleWebSignIn, nativeApple, nativeGoogle, nativeZalo, postSignIn, renderGoogleButton,
} from '../lib/socialAuth';

export const PROVIDER_LABELS = { zalo: 'Zalo', google: 'Google', apple: 'Apple' };
const BUTTON = 'btn h-[52px] w-full rounded-full border text-base font-semibold';

function ZaloLogo() {
  return (
    <svg viewBox="0 0 48 48" className="size-6" aria-hidden="true">
      <rect width="48" height="48" rx="12" fill="#fff" />
      <text x="24" y="31" textAnchor="middle" fontFamily="Arial, sans-serif" fontWeight="700" fontSize="17" fill="#0068ff">Zalo</text>
    </svg>
  );
}

function GoogleLogo() {
  return (
    <svg viewBox="0 0 48 48" className="size-5" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

function AppleLogo() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" fill="currentColor" aria-hidden="true">
      <path d="M16.37 12.6c-.02-2.3 1.88-3.4 1.96-3.46-1.07-1.56-2.73-1.78-3.32-1.8-1.41-.14-2.76.83-3.48.83-.72 0-1.82-.81-3-.79-1.54.02-2.96.9-3.76 2.27-1.6 2.78-.41 6.9 1.15 9.15.76 1.1 1.67 2.34 2.86 2.3 1.15-.05 1.58-.74 2.97-.74 1.38 0 1.77.74 2.98.72 1.23-.02 2.01-1.12 2.76-2.23.87-1.28 1.23-2.52 1.25-2.58-.03-.01-2.4-.92-2.42-3.66zM14.1 5.84c.63-.77 1.06-1.83.94-2.89-.91.04-2.01.61-2.67 1.37-.58.67-1.09 1.75-.95 2.78 1.01.08 2.05-.51 2.68-1.26z" />
    </svg>
  );
}

// The provider's logo on a square tile, for lists (Settings › Cách đăng nhập, the disconnect sheet).
export function ProviderTile({ provider, size = 'size-11' }) {
  if (provider === 'zalo') {
    return <span className={`grid ${size} shrink-0 place-items-center rounded-[12px] bg-[#0068ff] text-[11px] font-bold text-white`}>Zalo</span>;
  }
  if (provider === 'apple') {
    return <span className={`grid ${size} shrink-0 place-items-center rounded-[12px] bg-[#17201b] text-white`}><AppleLogo /></span>;
  }
  return <span className={`grid ${size} shrink-0 place-items-center rounded-[12px] border border-line bg-white`}><GoogleLogo /></span>;
}

// In the app, Google needs its iOS client (the native sheet is tied to the app's bundle id).
export const providerAvailable = (providers, provider, native) => !!providers?.[provider]
  && !(native && provider === 'google' && !providers.google.ios_client_id);

// Signing in (or connecting, intent "link") with a provider, wherever its button is drawn.
//   native: { apiUrl, siteUrl } inside the app, null on the website. token: the app's, when connecting.
//   start(provider) for Zalo, Apple and the app's Google; googleToken(idToken) for Google's website button.
//   onDone(data, provider): the server's answer; on the website sign-in it holds { redirect }.
export function useProviderSignIn({ providers, native = null, intent = 'sign_in', token = null, onDone }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const apiUrl = native?.apiUrl || '';

  const run = async (provider, getBody) => {
    setBusy(provider);
    setError(null);
    try {
      const body = await getBody();
      await onDone?.(await postSignIn({ apiUrl, provider, body, intent, token, native: !!native }), provider);
    } catch (e) {
      if (!(e instanceof SignInCancelled)) setError(e instanceof TypeError ? 'Không kết nối được máy chủ, kiểm tra mạng rồi thử lại' : e.message);
    } finally {
      setBusy(null);
    }
  };

  const start = (provider) => {
    if (provider === 'zalo' && !native) {
      // Website: a full-page trip to Zalo and back (the server signs in or connects, then redirects).
      window.location.href = intent === 'link' ? '/auth/zalo?intent=link' : '/auth/zalo';
    } else if (provider === 'zalo') {
      run('zalo', async () => ({ code: await nativeZalo(native.siteUrl) }));
    } else if (provider === 'apple') {
      run('apple', () => (native ? nativeApple(providers) : appleWebSignIn(providers.apple.service_id)));
    } else if (provider === 'google') {
      run('google', () => nativeGoogle(providers));
    }
  };

  const googleToken = (idToken) => run('google', async () => ({ id_token: idToken }));
  return { busy, error, start, googleToken };
}

// On the website Google only signs in through a button it draws itself (Google Identity Services).
// With `cover`, that button is laid, invisible, over our own (`children`) so a list row keeps its look.
export function GoogleWebButton({ clientId, onToken, cover = false, children = null }) {
  const ref = useRef(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;
  const [failed, setFailed] = useState(null);
  useEffect(() => {
    renderGoogleButton(ref.current, clientId, (idToken) => onTokenRef.current(idToken)).catch((e) => setFailed(e.message));
  }, [clientId]);
  if (cover) {
    return (
      <span className="relative inline-flex">
        {children}
        <span ref={ref} className="absolute inset-0 overflow-hidden opacity-[0.01]" title={failed || undefined} />
      </span>
    );
  }
  return (
    <div>
      <div ref={ref} className="flex min-h-[44px] justify-center" />
      {failed && <p className="text-center text-xs text-muted">{failed}</p>}
    </div>
  );
}

// Zalo, Google and Apple buttons: the login page, the app's login screen and the "connect Zalo" prompt.
// `providers` comes from /api/auth/providers (only configured ones show); `only` limits them further.
export default function SocialButtons({ providers, native = null, intent = 'sign_in', token = null, onDone, only = null }) {
  const { busy, error, start, googleToken } = useProviderSignIn({ providers, native, intent, token, onDone });
  const show = (p) => providerAvailable(providers, p, native) && (!only || only.includes(p));
  const verb = intent === 'link' ? 'Kết nối' : 'Tiếp tục với';
  const spinner = <span className="loading loading-spinner loading-sm" />;

  return (
    <div className="flex flex-col gap-3">
      {show('zalo') && (
        <button type="button" onClick={() => start('zalo')} disabled={!!busy}
          className={`${BUTTON} border-0 bg-[#0068ff] text-white hover:bg-[#0057d6]`}>
          {busy === 'zalo' ? spinner : <ZaloLogo />} {verb} Zalo
        </button>
      )}
      {show('google') && (native
        ? (
          <button type="button" disabled={!!busy} className={`${BUTTON} border-base-300 bg-white text-[#1f1f1f]`} onClick={() => start('google')}>
            {busy === 'google' ? spinner : <GoogleLogo />} {verb} Google
          </button>
        )
        : <GoogleWebButton clientId={providers.google.web_client_id} onToken={googleToken} />)}
      {show('apple') && (
        <button type="button" disabled={!!busy} className={`${BUTTON} border-0 bg-black text-white hover:bg-[#222]`} onClick={() => start('apple')}>
          {busy === 'apple' ? spinner : <AppleLogo />} {verb} Apple
        </button>
      )}
      {error && <p role="alert" className="text-center text-sm font-medium text-error">{error}</p>}
    </div>
  );
}
